import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, ChevronRight, CheckCircle, Upload, Loader2,
  ImageOff, Home, MapPin, DollarSign, BedDouble, Bath, Maximize2,
  Star, Sparkles, Pencil, Mountain, Users, Leaf, Zap, ChefHat,
  Sofa, Palette, SlidersHorizontal, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  AMENITY_SECTIONS, STYLE_OPTIONS,
  KITCHEN_SECTIONS, FURNISHING_STATUS, FURNISHING_INCLUDES,
  EMPTY_AMENITIES, EMPTY_KITCHEN, EMPTY_FURNISHING,
} from '../utils/canonical';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCENT = '#0A192F';
const GREEN = '#10B981';
const TOTAL_STEPS = 9;

// ─── Canonical vocabulary ──────────────────────────────────────────────────────
const PROPERTY_TYPES = [
  { id: 'single_family', label: 'Single Family',  icon: '🏠' },
  { id: 'condo',         label: 'Condo',          icon: '🏢' },
  { id: 'townhouse',     label: 'Townhouse',       icon: '🏘️' },
  { id: 'multi_family',  label: 'Multi-Family',    icon: '🏗️' },
  { id: 'land',          label: 'Land / Lot',      icon: '🌿' },
  { id: 'villa',         label: 'Villa',           icon: '🏰' },
];

const FEATURE_OPTIONS = [
  { key: 'has_pool',       label: 'Pool' },
  { key: 'has_garage',     label: 'Garage' },
  { key: 'has_solar',      label: 'Solar Panels' },
  { key: 'has_adu',        label: 'ADU / Casita' },
  { key: 'has_ev_charger', label: 'EV Charger' },
];

const EXTRA_FEATURES = [
  { key: 'fireplace',  label: 'Fireplace' },
  { key: 'gated',      label: 'Gated Entry' },
  { key: 'office',     label: 'Home Office' },
  { key: 'gym',        label: 'Home Gym' },
  { key: 'rv_parking', label: 'RV Parking' },
  { key: 'waterfront', label: 'Waterfront' },
];

const LIFESTYLE_TAGS = [
  { id: 'private',      label: 'Private & Quiet' },
  { id: 'entertainer',  label: 'Entertainer' },
  { id: 'family',       label: 'Family-Oriented' },
  { id: 'luxury',       label: 'Luxury' },
  { id: 'investment',   label: 'Investment' },
  { id: 'resort',       label: 'Resort-Style' },
  { id: 'walkable',     label: 'Walkable Area' },
  { id: 'off_grid',     label: 'Off-Grid' },
];

const BEDS  = [1, 2, 3, 4, 5, 6];
const BATHS = [1, 1.5, 2, 2.5, 3, 3.5];

const EMPTY = {
  property_type: 'single_family',
  address: '', city: '', state: '', zip: '',
  photos: [],
  beds: 3, baths: 2, sqft: '',
  price: '',
  has_hoa: false, hoa_amount: '',
  has_pool: false, has_solar: false, has_adu: false,
  has_garage: false, has_ev_charger: false,
  extra_features: [],
  lifestyle_tags: [],
  description: '',
  // Canonical v3
  amenities:  EMPTY_AMENITIES,
  style:      [],
  kitchen:    EMPTY_KITCHEN,
  furnishing: EMPTY_FURNISHING,
};

function buildFromListing(l) {
  if (!l) return EMPTY;
  return {
    property_type:  l.property_type  || 'single_family',
    address:        l.address        || '',
    city:           l.city           || '',
    state:          l.state          || '',
    zip:            l.zip            || '',
    photos:         l.photos         || [],
    beds:           l.beds           ?? 3,
    baths:          l.baths          ?? 2,
    sqft:           l.sqft ? String(l.sqft) : '',
    price:          l.price ? String(l.price) : '',
    has_hoa:        !!l.has_hoa,
    hoa_amount:     l.hoa_amount ? String(l.hoa_amount) : '',
    has_pool:       !!l.has_pool,
    has_solar:      !!l.has_solar,
    has_adu:        !!l.has_adu,
    has_garage:     !!l.has_garage,
    has_ev_charger: !!l.has_ev_charger,
    extra_features: l.extra_features || [],
    lifestyle_tags: l.lifestyle_tags || [],
    description:    l.description    || '',
    // Canonical v3
    amenities:  l.amenities  ? { ...EMPTY_AMENITIES, ...l.amenities }  : EMPTY_AMENITIES,
    style:      l.style      || [],
    kitchen:    l.kitchen    ? { ...EMPTY_KITCHEN,   ...l.kitchen }    : EMPTY_KITCHEN,
    furnishing: l.furnishing ? { ...EMPTY_FURNISHING, ...l.furnishing } : EMPTY_FURNISHING,
  };
}

// ─── Shared UI ─────────────────────────────────────────────────────────────────
function StepHeader({ step, title, sub }) {
  return (
    <div className="mb-6">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Step {step} of {TOTAL_STEPS}</div>
      <h2 className="text-xl font-black mb-1" style={{ color: ACCENT }}>{title}</h2>
      {sub && <p className="text-sm text-slate-500">{sub}</p>}
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className="px-3 py-2 rounded-full text-xs font-semibold border transition-all duration-150"
      style={active
        ? { background: ACCENT, color: 'white', borderColor: ACCENT }
        : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
      {children}
    </button>
  );
}

function NumPill({ value, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="w-11 h-11 rounded-xl text-sm font-bold transition-all duration-150 flex items-center justify-center"
      style={active
        ? { background: ACCENT, color: 'white' }
        : { background: '#F1F5F9', color: '#475569' }}>
      {value}
    </button>
  );
}

// ─── Step 1: Property Type ─────────────────────────────────────────────────────
function Step1({ w, set }) {
  return (
    <div>
      <StepHeader step={1} title="Property Type" sub="What kind of property are you listing?" />
      <div className="grid grid-cols-2 gap-3">
        {PROPERTY_TYPES.map(t => (
          <button key={t.id} type="button" onClick={() => set('property_type', t.id)}
            data-testid={`ptype-${t.id}`}
            className="p-4 rounded-2xl border-2 text-left transition-all"
            style={w.property_type === t.id
              ? { borderColor: ACCENT, background: '#F0F7FF' }
              : { borderColor: '#E2E8F0', background: 'white' }}>
            <span className="text-xl mb-2 block">{t.icon}</span>
            <div className="font-bold text-sm" style={{ color: ACCENT }}>{t.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 2: Address ───────────────────────────────────────────────────────────
function Step2({ w, set }) {
  return (
    <div>
      <StepHeader step={2} title="Property Address" sub="Where is the property located?" />
      <div className="space-y-3">
        <input type="text" placeholder="Street Address *" value={w.address}
          onChange={e => set('address', e.target.value)}
          data-testid="listing-address"
          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
        <div className="grid grid-cols-5 gap-2">
          <input type="text" placeholder="City *" value={w.city}
            onChange={e => set('city', e.target.value)}
            data-testid="listing-city"
            className="col-span-2 px-3 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
          <input type="text" placeholder="ST *" value={w.state}
            onChange={e => set('state', e.target.value.toUpperCase().slice(0, 2))}
            maxLength={2} data-testid="listing-state"
            className="col-span-1 px-3 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all text-center" />
          <input type="text" placeholder="ZIP" value={w.zip}
            onChange={e => set('zip', e.target.value)}
            className="col-span-2 px-3 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Photos ────────────────────────────────────────────────────────────
function Step3({ w, set, authFetch }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const fileRef = useRef(null);

  const uploadFiles = useCallback(async (files) => {
    const remaining = 8 - w.photos.length;
    if (remaining <= 0) return;
    const toUpload = Array.from(files).slice(0, remaining).filter(f => f.type.startsWith('image/'));
    if (!toUpload.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of toUpload) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await authFetch(`${API}/api/upload`, { method: 'POST', body: fd });
        if (r.ok) { const { url } = await r.json(); uploaded.push(url); }
      } catch (e) { console.error(e); }
    }
    if (uploaded.length) set('photos', [...w.photos, ...uploaded]);
    setUploading(false);
  }, [authFetch, w.photos, set]);

  const addUrl = () => {
    const url = urlInput.trim();
    if (url && w.photos.length < 8) { set('photos', [...w.photos, url]); setUrlInput(''); }
  };

  return (
    <div>
      <StepHeader step={3} title="Photos" sub="Great photos get more buyer attention." />
      {w.photos.length < 8 && (
        <div onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) uploadFiles(e.dataTransfer.files); }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => fileRef.current?.click()}
          data-testid="photo-drop-zone"
          className="border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all mb-4"
          style={{ borderColor: dragOver ? ACCENT : '#E2E8F0', background: dragOver ? '#F0F7FF' : 'white' }}>
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={22} className="animate-spin" style={{ color: ACCENT }} />
              <p className="text-sm text-slate-500">Uploading...</p>
            </div>
          ) : (
            <>
              <Upload size={22} className="mx-auto mb-2" style={{ color: ACCENT }} />
              <p className="text-sm font-semibold text-slate-700">Drop photos here or <span style={{ color: ACCENT }}>browse</span></p>
              <p className="text-xs text-slate-400 mt-0.5">Up to {8 - w.photos.length} more · JPEG, PNG, WebP</p>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { if (e.target.files.length) { uploadFiles(e.target.files); e.target.value = ''; } }} />
        </div>
      )}
      {w.photos.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-4">
          {w.photos.map((url, i) => (
            <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-slate-200 group"
              data-testid={`photo-preview-${i}`}>
              <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover"
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              <div className="w-full h-full hidden items-center justify-center bg-slate-100">
                <ImageOff size={14} className="text-slate-300" />
              </div>
              <button onClick={e => { e.stopPropagation(); set('photos', w.photos.filter((_, j) => j !== i)); }}
                data-testid={`photo-remove-${i}`}
                className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={9} />
              </button>
              {i === 0 && <div className="absolute bottom-0 left-0 right-0 text-center text-white text-xs font-bold py-0.5" style={{ background: ACCENT + 'CC' }}>Main</div>}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input type="url" placeholder="Or paste photo URL..." value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addUrl()}
          data-testid="photo-url-input"
          className="flex-1 px-3 py-2.5 rounded-xl border border-slate-200 text-xs outline-none focus:border-slate-400 transition-all" />
        <button type="button" onClick={addUrl} disabled={!urlInput.trim() || w.photos.length >= 8}
          className="px-4 py-2.5 rounded-xl text-xs font-bold text-white disabled:opacity-40 transition-all"
          style={{ background: ACCENT }}>Add</button>
      </div>
    </div>
  );
}

// ─── Step 4: Size & Specs ──────────────────────────────────────────────────────
function Step4({ w, set }) {
  return (
    <div>
      <StepHeader step={4} title="Size & Specs" sub="Key measurements that buyers filter on." />
      <div className="space-y-6">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-2">Bedrooms</label>
          <div className="flex gap-2 flex-wrap">
            {BEDS.map(b => <NumPill key={b} value={b === 6 ? '6+' : b} active={w.beds === b} onClick={() => set('beds', b)} />)}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-2">Bathrooms</label>
          <div className="flex gap-2 flex-wrap">
            {BATHS.map(b => <NumPill key={b} value={b === 3.5 ? '3.5+' : b} active={w.baths === b} onClick={() => set('baths', b)} />)}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-2">Square Footage</label>
          <div className="relative">
            <input type="number" placeholder="e.g. 2400" value={w.sqft}
              onChange={e => set('sqft', e.target.value)}
              data-testid="listing-sqft"
              className="w-full px-4 pr-14 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400">sqft</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 5: Price & Financials ────────────────────────────────────────────────
function Step5({ w, set }) {
  return (
    <div>
      <StepHeader step={5} title="Price & Financials" sub="Set the asking price and HOA details." />
      <div className="space-y-5">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-2">Asking Price *</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
            <input type="number" placeholder="e.g. 650000" value={w.price}
              onChange={e => set('price', e.target.value)}
              data-testid="listing-price"
              className="w-full pl-8 pr-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">HOA</label>
            <button type="button" onClick={() => set('has_hoa', !w.has_hoa)}
              data-testid="feature-has_hoa"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all border"
              style={w.has_hoa
                ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
              {w.has_hoa ? 'HOA: Yes' : 'No HOA'}
            </button>
          </div>
          {w.has_hoa && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">$</span>
                <input type="number" placeholder="Monthly HOA amount" value={w.hoa_amount}
                  onChange={e => set('hoa_amount', e.target.value)}
                  className="w-full pl-8 pr-16 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400">/mo</span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step 6: Features & Lifestyle ─────────────────────────────────────────────
function Step6({ w, set }) {
  const toggleExtra = (key) => {
    const curr = w.extra_features || [];
    set('extra_features', curr.includes(key) ? curr.filter(f => f !== key) : [...curr, key]);
  };
  const toggleLifestyle = (id) => {
    const curr = w.lifestyle_tags || [];
    set('lifestyle_tags', curr.includes(id) ? curr.filter(t => t !== id) : [...curr, id]);
  };
  return (
    <div>
      <StepHeader step={6} title="Features & Lifestyle" sub="What makes this home special?" />
      <div className="space-y-6">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-3">Key Features</label>
          <div className="flex flex-wrap gap-2">
            {FEATURE_OPTIONS.map(f => (
              <button key={f.key} type="button" onClick={() => set(f.key, !w[f.key])}
                data-testid={`feature-${f.key}`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all border"
                style={w[f.key]
                  ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                  : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
                {w[f.key] && <CheckCircle size={10} />}{f.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-3">Additional Features</label>
          <div className="flex flex-wrap gap-2">
            {EXTRA_FEATURES.map(f => (
              <Pill key={f.key} active={(w.extra_features || []).includes(f.key)} onClick={() => toggleExtra(f.key)}>
                {f.label}
              </Pill>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-widest block mb-3">Lifestyle Tags</label>
          <div className="flex flex-wrap gap-2">
            {LIFESTYLE_TAGS.map(t => (
              <Pill key={t.id} active={(w.lifestyle_tags || []).includes(t.id)} onClick={() => toggleLifestyle(t.id)}>
                {t.label}
              </Pill>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 7: Amenities & Style ─────────────────────────────────────────────────
const ICON_MAP = { Mountain, Users, Home, Leaf, Zap, Sparkles, Palette, ChefHat, Sofa, SlidersHorizontal };

function AmenitySectionAccordion({ section, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON_MAP[section.icon] || SlidersHorizontal;
  const count = (selected || []).length;
  return (
    <div className="border border-slate-100 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left">
        <div className="flex items-center gap-2">
          <Icon size={13} style={{ color: ACCENT }} />
          <span className="text-xs font-bold" style={{ color: ACCENT }}>{section.label}</span>
          {count > 0 && (
            <span className="text-xs font-black px-2 py-0.5 rounded-full text-white" style={{ background: GREEN }}>{count}</span>
          )}
        </div>
        {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden border-t border-slate-100">
            <div className="px-4 py-3 bg-slate-50 flex flex-wrap gap-1.5">
              {section.items.map(item => {
                const active = (selected || []).includes(item.id);
                return (
                  <button key={item.id} type="button" onClick={() => onToggle(item.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                    style={active
                      ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                      : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
                    {active && <CheckCircle size={9} />}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Step7({ w, set }) {
  const toggleAmenity = (cat, id) => {
    const prev = w.amenities || EMPTY_AMENITIES;
    const arr  = prev[cat] || [];
    set('amenities', { ...prev, [cat]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };
  const toggleStyle = (id) => {
    const prev = w.style || [];
    set('style', prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const amenityCount = Object.values(w.amenities || {}).flat().length;
  return (
    <div>
      <StepHeader step={7} title="Amenities & Style" sub="Help buyers discover unique attributes of this property." />
      <div className="space-y-4">
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Amenities <span className="text-slate-300 font-normal">(optional)</span></p>
          <div className="space-y-2">
            {AMENITY_SECTIONS.map(sec => (
              <AmenitySectionAccordion
                key={sec.key}
                section={sec}
                selected={(w.amenities || {})[sec.key] || []}
                onToggle={(id) => toggleAmenity(sec.key, id)}
              />
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Architectural Style <span className="text-slate-300 font-normal">(optional)</span></p>
          <div className="flex flex-wrap gap-1.5">
            {STYLE_OPTIONS.map(s => {
              const active = (w.style || []).includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => toggleStyle(s.id)}
                  data-testid={`listing-style-${s.id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                  style={active
                    ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                    : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 8: Kitchen & Move-In Ready ──────────────────────────────────────────
function Step8({ w, set }) {
  const toggleKitchen = (cat, id) => {
    const prev = w.kitchen || EMPTY_KITCHEN;
    const arr  = prev[cat] || [];
    set('kitchen', { ...prev, [cat]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };
  const toggleFurnInclude = (id) => {
    const prev = w.furnishing || EMPTY_FURNISHING;
    const arr  = prev.includes || [];
    set('furnishing', { ...prev, includes: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };
  const furnishing = w.furnishing || EMPTY_FURNISHING;
  return (
    <div>
      <StepHeader step={8} title="Kitchen & Move-In Ready" sub="Buyers filter on kitchen quality and furnishing." />
      <div className="space-y-5">
        {KITCHEN_SECTIONS.map(sec => (
          <div key={sec.key}>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{sec.label}</p>
            <div className="flex flex-wrap gap-1.5">
              {sec.items.map(item => {
                const active = ((w.kitchen || {})[sec.key] || []).includes(item.id);
                return (
                  <button key={item.id} type="button" onClick={() => toggleKitchen(sec.key, item.id)}
                    data-testid={`listing-kitchen-${item.id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                    style={active
                      ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                      : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Furnishing Status</p>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {FURNISHING_STATUS.map(s => (
              <button key={s.id} type="button"
                data-testid={`listing-furnishing-${s.id}`}
                onClick={() => set('furnishing', { ...furnishing, status: furnishing.status === s.id ? '' : s.id })}
                className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                style={furnishing.status === s.id
                  ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                  : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
                {s.label}
              </button>
            ))}
          </div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Includes</p>
          <div className="flex flex-wrap gap-1.5">
            {FURNISHING_INCLUDES.map(i => {
              const active = (furnishing.includes || []).includes(i.id);
              return (
                <button key={i.id} type="button" onClick={() => toggleFurnInclude(i.id)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-all"
                  style={active
                    ? { background: ACCENT, color: 'white', borderColor: ACCENT }
                    : { background: 'white', color: '#475569', borderColor: '#E2E8F0' }}>
                  {i.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 9: Description + Review ─────────────────────────────────────────────
function Step9({ w, set }) {
  const typeLabel = PROPERTY_TYPES.find(t => t.id === w.property_type)?.label || w.property_type;
  const features = FEATURE_OPTIONS.filter(f => w[f.key]).map(f => f.label);
  const amenityCount = Object.values(w.amenities || {}).flat().length;
  const styleCount   = (w.style || []).length;
  const kitchenCount = Object.values(w.kitchen || {}).flat().length;
  return (
    <div>
      <StepHeader step={9} title="Description & Review" sub="Write a compelling description, then publish." />
      <div className="space-y-5">
        <textarea placeholder="Describe this property — highlight unique selling points, recent upgrades, neighborhood perks..."
          value={w.description} onChange={e => set('description', e.target.value)}
          data-testid="listing-description"
          rows={4}
          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-slate-400 transition-all resize-none" />
        {/* Review summary */}
        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 space-y-2">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Review Summary</p>
          <ReviewRow label="Type"     value={typeLabel} />
          <ReviewRow label="Address"  value={`${w.address}, ${w.city}, ${w.state} ${w.zip}`} />
          <ReviewRow label="Price"    value={w.price ? `$${parseInt(w.price).toLocaleString()}` : '—'} />
          <ReviewRow label="Size"     value={`${w.beds} bd · ${w.baths} ba · ${w.sqft ? parseInt(w.sqft).toLocaleString() : '—'} sqft`} />
          <ReviewRow label="Photos"   value={`${w.photos.length} added`} />
          {features.length > 0 && <ReviewRow label="Features" value={features.join(', ')} />}
          {(w.lifestyle_tags || []).length > 0 && <ReviewRow label="Lifestyle" value={w.lifestyle_tags.join(', ')} />}
          {amenityCount > 0 && <ReviewRow label="Amenities" value={`${amenityCount} selected`} />}
          {styleCount > 0 && <ReviewRow label="Style" value={(w.style || []).join(', ')} />}
          {kitchenCount > 0 && <ReviewRow label="Kitchen" value={`${kitchenCount} attributes`} />}
          {(w.furnishing || {}).status && <ReviewRow label="Furnishing" value={(w.furnishing || {}).status.replace(/_/g, ' ')} />}
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs font-semibold text-slate-400 w-16 flex-shrink-0">{label}</span>
      <span className="text-xs text-slate-700 font-medium">{value || '—'}</span>
    </div>
  );
}

const STEPS = [Step1, Step2, Step3, Step4, Step5, Step6, Step7, Step8, Step9];

// ─── Main Wizard ───────────────────────────────────────────────────────────────
export function ListingWizard({ isOpen, onClose, onSuccess, editListing = null }) {
  const { authFetch } = useAuth();
  const [step, setStep] = useState(1);
  const [w, setWState] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!editListing;

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setWState(buildFromListing(editListing));
      setError('');
    }
  }, [isOpen, editListing]);

  const set = useCallback((key, val) => setWState(prev => ({ ...prev, [key]: val })), []);

  const goNext = () => { if (step < TOTAL_STEPS) setStep(s => s + 1); };
  const goBack = () => { if (step > 1) setStep(s => s - 1); };

  const validate = () => {
    if (!w.address || !w.city || !w.state) return 'Address, city and state are required';
    if (!w.price) return 'Asking price is required';
    if (!w.sqft) return 'Square footage is required';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError('');
    try {
      // Build canonical feature_flags from flat fields + extras
      const allFeatures = [
        ...(w.has_pool       ? ['pool']       : []),
        ...(w.has_garage     ? ['garage']     : []),
        ...(w.has_solar      ? ['solar']      : []),
        ...(w.has_adu        ? ['adu']        : []),
        ...(w.has_ev_charger ? ['ev_charger'] : []),
        ...(!w.has_hoa       ? ['no_hoa']     : []),
        ...(w.extra_features || []),
      ];
      const body = {
        address:        w.address,
        city:           w.city,
        state:          w.state,
        zip:            w.zip,
        price:          parseInt(w.price),
        beds:           w.beds,
        baths:          w.baths,
        sqft:           parseInt(w.sqft),
        description:    w.description,
        photos:         w.photos.filter(p => p && p.trim()),
        has_pool:       w.has_pool,
        has_solar:      w.has_solar,
        has_adu:        w.has_adu,
        has_garage:     w.has_garage,
        has_hoa:        w.has_hoa,
        hoa_amount:     w.has_hoa ? (parseInt(w.hoa_amount) || 0) : 0,
        has_ev_charger: w.has_ev_charger,
        property_type:  w.property_type,
        lifestyle_tags: w.lifestyle_tags || [],
        feature_flags:  { features: allFeatures },
        // Canonical v3
        amenities:  w.amenities  || EMPTY_AMENITIES,
        style:      w.style      || [],
        kitchen:    w.kitchen    || EMPTY_KITCHEN,
        furnishing: w.furnishing || EMPTY_FURNISHING,
      };
      const url    = isEdit ? `${API}/api/listings/${editListing.id}` : `${API}/api/listings`;
      const method = isEdit ? 'PUT' : 'POST';
      const r = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        onSuccess();
        onClose();
      } else {
        const d = await r.json();
        setError(d.detail || (isEdit ? 'Failed to update listing' : 'Failed to create listing'));
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setSaving(false);
  };

  const StepComp = STEPS[step - 1];
  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />

          {/* Full-screen wizard panel */}
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 flex flex-col shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0"
              style={{ background: ACCENT }}>
              <div className="flex items-center gap-2">
                {isEdit && <Pencil size={13} className="text-white/70" />}
                <span className="text-white font-bold text-sm">
                  {isEdit ? 'Edit Listing' : 'Create Listing'}
                </span>
                <span className="text-white/50 text-xs ml-1">— {step}/{TOTAL_STEPS}</span>
              </div>
              <button onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25 transition-colors">
                <X size={15} className="text-white" />
              </button>
            </div>

            {/* Progress bar */}
            <div className="h-1 bg-slate-100 flex-shrink-0">
              <motion.div className="h-full" style={{ background: GREEN }}
                animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
            </div>

            {/* Step content */}
            <div className="flex-1 overflow-y-auto px-5 py-6">
              <AnimatePresence mode="wait">
                <motion.div key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}>
                  <StepComp w={w} set={set} authFetch={authFetch} />
                </motion.div>
              </AnimatePresence>

              {error && (
                <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700"
                  data-testid="listing-form-error">{error}</div>
              )}
            </div>

            {/* Footer navigation */}
            <div className="px-5 py-4 border-t border-slate-100 flex-shrink-0 bg-white flex gap-3">
              {step > 1 && (
                <button type="button" onClick={goBack}
                  className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                  <ChevronLeft size={15} />Back
                </button>
              )}
              {step < TOTAL_STEPS ? (
                <button type="button" onClick={goNext} data-testid="wizard-next-btn"
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: ACCENT }}>
                  Continue <ChevronRight size={15} />
                </button>
              ) : (
                <button type="button" onClick={handleSave} disabled={saving}
                  data-testid="submit-listing-btn"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: GREEN }}>
                  <CheckCircle size={15} />
                  {saving ? (isEdit ? 'Saving...' : 'Publishing...') : (isEdit ? 'Save Changes' : 'Publish Listing')}
                </button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
